// src/pages/profile/Profile.tsx
import React, { useState, useRef } from 'react';
import {
  User, Mail, Phone, Building2, Shield,
  Camera, Save, Clock,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Input } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/auth';
import { getAvatarUrl } from '../../utils/url';
import toast from 'react-hot-toast';

const Profile: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  // State to force re-render avatar after upload
  const [avatarKey, setAvatarKey] = useState(Date.now());

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedUser = await authService.updateProfile({
        name: formData.name,
        phone: formData.phone,
      });
      setUser(updatedUser);
      toast.success('Profil berhasil diperbarui');
    } catch (e) {
      toast.error('Gagal memperbarui profil');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format file harus JPG, PNG, GIF, atau WebP');
      return;
    }

    const toastId = toast.loading('Mengupload avatar...');
    try {
      const updatedUser = await authService.updateProfile({
        name: formData.name,
        phone: formData.phone || user?.phone || '',
        avatar: file
      });
      setUser(updatedUser);
      // Force re-render avatar with new key
      setAvatarKey(Date.now());
      toast.success('Avatar berhasil diperbarui', { id: toastId });
    } catch (e: any) {
      console.error('Avatar upload error:', e);
      const errorMsg = e.response?.data?.message || 'Gagal mengupload avatar';
      toast.error(errorMsg, { id: toastId });
    }

    // Reset input to allow uploading same file again
    if (e.target) e.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const initials = formData.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  // Use utility function with avatarKey for cache busting
  const avatarUrl = getAvatarUrl(user?.avatar, user?.updated_at || avatarKey.toString());

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Profil Saya' }]} />

      <h1 className="text-2xl font-black text-teal-dark tracking-tight">Profil Saya</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Avatar & Info */}
        <Card className="text-center">
          <div className="relative inline-block mb-4">
            <div className="w-24 h-24 rounded-full bg-teal-main text-white flex items-center justify-center text-3xl font-black mx-auto overflow-hidden">
              {avatarUrl ? (
                <img
                  key={avatarKey} // Force re-render
                  src={avatarUrl}
                  alt={formData.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                initials
              )}
              {/* Fallback initials if image error */}
              {avatarUrl && (
                <span className="hidden absolute inset-0 flex items-center justify-center text-3xl font-black">
                  {initials}
                </span>
              )}
            </div>
            <button
              onClick={triggerFileInput}
              className="absolute bottom-0 right-0 w-8 h-8 bg-teal-main text-white rounded-full flex items-center justify-center shadow-lg hover:bg-teal-light transition-colors"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </div>
          <h2 className="text-lg font-bold text-neutral-text-primary">{formData.name}</h2>
          <p className="text-sm text-neutral-text-secondary">{user?.role?.display_name || '-'}</p>
          <div className="mt-4 space-y-2 text-sm text-left">
            <div className="flex items-center gap-2 text-neutral-text-secondary">
              <Building2 className="w-4 h-4" /> <span>{user?.unit?.name || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-neutral-text-secondary">
              <Mail className="w-4 h-4" /> <span>{formData.email}</span>
            </div>
            <div className="flex items-center gap-2 text-neutral-text-secondary">
              <Clock className="w-4 h-4" /> <span>Login terakhir: {user?.last_login_at ? new Date(user.last_login_at).toLocaleString('id-ID') : '-'}</span>
            </div>
          </div>
        </Card>

        {/* Right: Edit Form */}
        <div className="lg:col-span-2">
          <Card>
            <h3 className="text-base font-bold text-teal-dark mb-4">Informasi Pribadi</h3>
            <div className="space-y-4">
              <Input
                label="Nama Lengkap"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                icon={<User className="w-4 h-4" />}
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                icon={<Mail className="w-4 h-4" />}
                disabled
                helpText="Email tidak dapat diubah, hubungi admin"
              />
              <Input
                label="No. Telepon"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                icon={<Phone className="w-4 h-4" />}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Role</p>
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-teal-main" />
                    <span className="font-medium">{user?.role?.display_name || '-'}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Unit</p>
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-teal-main" />
                    <span className="font-medium">{user?.unit?.name || '-'}</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button variant="primary" onClick={handleSave} loading={saving} icon={<Save className="w-4 h-4" />}>
                  Simpan Perubahan
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
